import React, { useRef, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { useNavigate } from 'react-router'


import {
  buildAreaKey } from 'Utils/mapUtils'
import { updateFilterQuery, removeQueries, urlSearchParamsToObj } from 'Utils/routerUtils'
import { getForOfferUrl } from 'Utils'
import Spinner from 'Components/Spinner'
import { areaParamList } from 'Defaults/map'
import { MainAlgoAutoComplete } from 'Components/MainAlgoAutoComplete'
import SelectedFilterTags from 'Components/SelectedFilterTagsHeader'
import TagManager from 'Components/TagManager'

import useOnClickOutside from 'Hooks/useOnClickOutside'

import { BodyOverflowHidden } from 'Hooks/useBodyOverflowHidden'
import { IS_MRLOFT } from 'Config/config'
import { browserStorage } from 'Redux/services/storage'

import PrevSearches, { PrevSearchManager } from './components/PrevSearches'
import PopularAreasSearchApiContainer from './components/PopularAreasSearch/PopularAreasSearchApiContainer'
import DiscoverAreaCta from './components/DiscoverAreaCta'

import {
  _FilterSearch,
  _FilterSearchInnerBox,
  _ExcludeClearBtnWidth,
  _AreaLabel,
  _SearchContainer,
  _MultipleAreaLabel,
  _MagnifyingGlassLeft,
  _MiddleRow,
  _MagnifyingGlassIcon,
  _CommonAreaName,
  _InputBox,
  _BottomRow,
  _MiddleRowClearBtn,
  _AreaSelected,
  _DoneBtn,
  _SelectedTagsDisplay,
  _SelectedAreaLabelContainer,
  _LabelsContainer,
} from './styles'


const bs = browserStorage()


const SEARCH_INPUT_PLACEHOLDER = IS_MRLOFT ?
  'Find a Toronto Loft by Name, Address or MLS #' :
  'City, Building, Neighborhood, Address..'

const getAreaUrl = ({ baseUrl, lookups, searchObj }) => {
  const searchObjWithoutArea = R.omit(areaParamList, searchObj)
  const offerUrl = getForOfferUrl(lookups, searchObjWithoutArea)
  return `${baseUrl}/${offerUrl}` + (!R.isEmpty(searchObjWithoutArea) ?
    `?${new URLSearchParams(searchObjWithoutArea).toString()}` : '')
}

const REMOVE_AREA = true


const FilterSearch = ({
  lastSearch, updateSelectedFiltersList, googleAnalyticsEvent,
  setFilterSearchOpenState, areaFilterTags,
  calculateLcaAndSelectedAreas,
  isFilterSearchOpen,
  lookups,
  winWidth,
  userCurrentArea,

  result,
  isFetching,
  loadAutoComplete,
  typingStart,
  lookupFilterTag,
  lookupFetching,
  winHeight,
  className,
  setDiscoveryAreaOpenState,
  areas,
  isMobileApp,
  fromNav,
  setSelectedAreas,
  updateAlgoliaData,
}) => {
  const navigate = useNavigate()
  const prevSearchManagerRef = useRef(null)

  const handleClickOutside = () => {
    setFilterSearchOpenState(false)
  }

  const innerBoxRef = useRef(null)
  useOnClickOutside(innerBoxRef, handleClickOutside)

  const wrapperRef = useRef(null)

  prevSearchManagerRef.current = useMemo(() => {
    PrevSearchManager({
      maxCount: 9,
      storageKey: 'prevSearches',
      localStorage: bs,
    })
  }, [])

  const [stateUserPrevSearches, setStateUserPrevSearches] = useState(prevSearchManagerRef.current?.getSearches())

  const [inputValue, setInputValue] = useState('')


  useEffect(() => () => {
    updateSelectedFiltersList([])
  }, [])

  useEffect(() => {
    const prevSearches = prevSearchManagerRef.current?.getSearches()
    if(prevSearches !== stateUserPrevSearches) {
      setStateUserPrevSearches(prevSearches)
    }
  }, [lastSearch])

  const onGoAreaBtnClick = e => {
    e.stopPropagation()

    googleAnalyticsEvent({
      category: 'search',
      action: ['Search Bar Done Clicked'],
    })
    return setFilterSearchOpenState(false)
  }

  const removeSelectedFilter = (searchParamType, preSelectedFilters) => {
    const hasSameFilter = R.propEq('filter_type', searchParamType)
    return R.reject(hasSameFilter, preSelectedFilters)
  }

  const onTagClicked = filterObj => {
    let newSelectedFilters = areaFilterTags ? [...areaFilterTags] : []
    newSelectedFilters = removeSelectedFilter(filterObj.filter_type, newSelectedFilters)

    const isAreaType = R.contains(filterObj.filter_type, areaParamList)
    // if there is only lookup area tag is exist and try to remove
    if(isAreaType) {
      calculateLcaAndSelectedAreas(buildAreaKey(filterObj.type, filterObj.filter_value), REMOVE_AREA)
    }
    // To Update the array of filters in redux
    updateSelectedFiltersList(newSelectedFilters)
    // //////////////////////////////////////////////////////
    const locationQ = lastSearch ?
      urlSearchParamsToObj(lastSearch) : {}
    let newFilterQuery

    newFilterQuery = locationQ[filterObj.filter_type] ? locationQ[filterObj.filter_type].split(',') : []
    newFilterQuery = R.reject(R.equals(filterObj.filter_value.toString()), newFilterQuery)

    updateFilterQuery(filterObj.filter_type, newFilterQuery)

    // Close the filter dropdown after removing a tag
    if(isFilterSearchOpen) setFilterSearchOpenState(false)
  }

  const onAreaSelected = (suggestion, newSelectedFilters) => {
    const isSameFilter = R.propEq('filter_type', suggestion.filter_type) &&
      R.propEq('filter_value', suggestion.filter_value.toString())
    if(!R.any(isSameFilter)(newSelectedFilters)) {
      // area selection on search page need to be calculated before added to the url
      // such as lca and bubble down/top
      const areaType = (suggestion.filter_type.slice(0, 1).toUpperCase() + suggestion.filter_type.slice(1))
      calculateLcaAndSelectedAreas(areaType.replace('id', suggestion.filter_value), !REMOVE_AREA)
    }
  }


  const onInputChange = (value) => {
    setInputValue(value)
  }

  const onSuggestionSelect = (suggestion, saveSuggestion = true) => {
    // save previously clicked search item
    if(prevSearchManagerRef.current && saveSuggestion) {
      prevSearchManagerRef.current.addNewSearch(suggestion)
    }
    const newSelectedFilters = [...(areaFilterTags || [])]
    const locationQ = lastSearch ?
      urlSearchParamsToObj(lastSearch) : {}
    const newFilterQuery = new Set(locationQ[suggestion.filter_type]?.split(',') || [])
    // reset input data to show previous search
    onInputChange('')
    // if value is already exist, do nothing
    if((newFilterQuery.size !== 0) && newFilterQuery.has(suggestion.filter_value)) {
      return null
    }

    if(lookups.component === 'Search') {
      if(R.contains(suggestion.filter_type, ['mls_number', 'building_id', 'address_id', 'name', 'school_name'])) {
        navigate('/' + suggestion.url)
        setFilterSearchOpenState(false)
      } else {
        onAreaSelected(suggestion, newSelectedFilters)
      }
    } else {
      if(areaParamList.indexOf(suggestion.filter_type) > -1) {
        onAreaSelected(suggestion, newSelectedFilters)
      }
      setFilterSearchOpenState(false)
      let url = ''
      const isAreaType = R.contains(suggestion.filter_type, areaParamList)
      if(isAreaType) {
        url = getAreaUrl({
          lookups,
          searchObj: {},
          baseUrl: suggestion.url,
        })
      } else {
        url = suggestion.url
      }

      navigate('/' + (url || suggestion.url))
    }

    googleAnalyticsEvent({
      category: 'search',
      action: [
        'Search History Item Selected',
        suggestion.filter_type,
      ],
    })


    // Close the filter dropdown after adding area
    if(isFilterSearchOpen) setFilterSearchOpenState(false)
  }

  const onCloseSearch = () => {
    const body = document.querySelector('body')
    const isUserTabbing = body.classList.contains('userIsTabbing')
    if(isUserTabbing) setFilterSearchOpenState(false)

    setInputValue('')
  }

  const onFilterSearchClicked = () => {
    googleAnalyticsEvent({
      category: 'search',
      action: ['Search Bar Click'],
    })
    if(!isFilterSearchOpen) {
      setFilterSearchOpenState(true)
    }
  }


  const showMobileVersion = useMemo(() => winWidth <= 1024, [winWidth])


  const clearSearchHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()

    googleAnalyticsEvent({
      category: 'search',
      action: ['Search Bar Clear Clicked'],
    })
    // without reseting redux there is some delay since url update then remove tag
    calculateLcaAndSelectedAreas(
      `${userCurrentArea.areaType}_${userCurrentArea.areaId}`,
    )

    updateSelectedFiltersList([])
    setSelectedAreas(null)
    removeQueries(areaParamList)
  }


  const renderTags = state => {
    if(!isFilterSearchOpen) return null

    return (
      // Only show tags when there is params on url
      <SelectedFilterTags
        autoCompleteTag
        showAutoCompleteMobileTag={showMobileVersion}
        lookupAreaTag={
          !state.areaFilterTags?.length
            ? !lookupFilterTag?.length
              ? [{ title: areas?.label }]
              : lookupFilterTag
            : []
        }
        selectedFilters={state.areaFilterTags}
        isFilterSearchOpen={isFilterSearchOpen}
        onTagClicked={onTagClicked}
        clearSearchHandler={clearSearchHandler}
        isRemovable
      />
    )
  }


  const selectedFiltersLength = areaFilterTags && areaFilterTags.length
  const commonParentLabel = areas.label || 'Toronto'
  const showMobileFilterOpen = showMobileVersion && isFilterSearchOpen
  const areaTagCount = areaFilterTags ? areaFilterTags.length : 0
  let lookupTagCount = 0
  // only if current user area not equal to lookupFilterTag
  // user area is above sublocality
  if(!areaTagCount && lookupFilterTag && lookupFilterTag.length) {
    const { area_id: lookupAreaId, type } = lookupFilterTag[0]
    const { areaId, areaType } = userCurrentArea
    if(lookupAreaId !== areaId && type !== areaType) {
      lookupTagCount += 1
    }
  }
  const totalAreaCount = areaTagCount + lookupTagCount
  const showSpinner = lookupFetching
  const noTagVisible = !areaTagCount


  /**
   *
   *  It renders currently selected area name(no area params on url)
   *  shows count on multiple area selected
   */
  const renderSelectedAreaLabel = () => {
    if(selectedFiltersLength > 1) {
      return (
        <_MultipleAreaLabel isFilterSearchOpen={isFilterSearchOpen}>
          {`${lookupFilterTag ? selectedFiltersLength : selectedFiltersLength} areas selected`}
        </_MultipleAreaLabel>
      )
    } if(lookupFilterTag && !R.isEmpty(lookupFilterTag) && lookupFilterTag[0].title) {
      if(commonParentLabel === lookupFilterTag[0].title) {
        return selectedFiltersLength === 1 ?
          <_MultipleAreaLabel isFilterSearchOpen={isFilterSearchOpen}>
            {areaFilterTags[0].title}
          </_MultipleAreaLabel>
          : null
      }
      return <_MultipleAreaLabel isFilterSearchOpen={isFilterSearchOpen}>{lookupFilterTag[0].title}</_MultipleAreaLabel>
    }
  }

  return (
    <_FilterSearch
      id="filterSearch"
      data-test-id="filterSearch"
      winHeight={winHeight}
      onSubmit={e => e.preventDefault()}
      autoComplete="off"
      isFilterSearchOpen={isFilterSearchOpen}
      className={className}
      aria-label="Top nav search bar"
      key={'filtersearch-' + areas.label}
      isMobileApp={isMobileApp}
      fromNav={fromNav}
      ref={innerBoxRef}
    >
      <BodyOverflowHidden haveBodyOverflowHidden={isFilterSearchOpen} />
      <_FilterSearchInnerBox
        tabIndex={0}
        role="search"
        data-test-id="filterSearchInnerBox"
        justify={'between'}
        onClick={onFilterSearchClicked}
        isFilterSearchOpen={isFilterSearchOpen}
        isMobileApp={isMobileApp}
        winHeight={winHeight}
      >
        <div>
          <_ExcludeClearBtnWidth
            isFilterSearchOpen={isFilterSearchOpen}
            showMobileFilterOpen={showMobileFilterOpen}
            fromNav={fromNav}
          >
            <_LabelsContainer>
              <_AreaLabel isFilterSearchOpen={isFilterSearchOpen} fromNav={fromNav}>
                <_MagnifyingGlassLeft name="#icon-search-glass" width={16} height={16} />
                {
                  // on open on mobile, label will show below input field
                  !(showMobileVersion && isFilterSearchOpen) && commonParentLabel
                }
              </_AreaLabel>
              {
                lookups.component === 'Search' && !isFilterSearchOpen &&
                <_SelectedAreaLabelContainer>
                  {
                    renderSelectedAreaLabel()
                  }
                </_SelectedAreaLabelContainer>
              }
            </_LabelsContainer>
          </_ExcludeClearBtnWidth>
          {isFilterSearchOpen ?
            <>
              <_InputBox>
                {
                  showMobileFilterOpen && totalAreaCount > 1 ? (
                    <_AreaSelected>
                      <_CommonAreaName>
                        {commonParentLabel} {totalAreaCount ? `: ${totalAreaCount} areas selected` : ''}
                      </_CommonAreaName>
                    </_AreaSelected>
                  ) : ''
                }

                <_MiddleRow>
                  <_SelectedTagsDisplay>
                    <TagManager
                      updateSelectedFiltersList={updateSelectedFiltersList}
                      lastSearch={lastSearch}
                      render={renderTags}
                    />
                  </_SelectedTagsDisplay>
                  {
                    !noTagVisible ? (
                      <_MiddleRowClearBtn
                        onMouseDown={clearSearchHandler}
                        onFocus={clearSearchHandler}
                      >
                        Clear
                      </_MiddleRowClearBtn>
                    ) : ''
                  }
                </_MiddleRow>
                <_BottomRow>
                  <_MagnifyingGlassIcon name="#icon-search-glass" width={15} height={15} />
                  <MainAlgoAutoComplete
                    multiSection
                    citySearchEnable
                    result={result}
                    // prevSearches={prevSearches}
                    isFetching={isFetching}
                    closeSearch={onCloseSearch}
                    onInputChange={onInputChange}
                    placeHolder={SEARCH_INPUT_PLACEHOLDER}
                    loadAutoComplete={loadAutoComplete}
                    typingStart={typingStart}
                    onSuggestionSelect={onSuggestionSelect}
                    isFilterSearchOpen={isFilterSearchOpen}
                    wrapperRef={wrapperRef.current}
                    // getInputRef={this.getInputRef}
                    updateAlgoliaData={updateAlgoliaData}
                    siteSearchIsOpen={isFilterSearchOpen}
                    updateSelectedFiltersList={updateSelectedFiltersList}
                    source="header"
                    isMobileApp={isMobileApp}
                  />
                  {
                    isFilterSearchOpen && (
                      <_DoneBtn
                        aria-label="search button"
                        onClick={onGoAreaBtnClick}
                        name={'header-search'}
                      >
                        {
                          showSpinner ?
                            (
                              <div><Spinner /></div>
                            ) : <span>Done</span>
                        }

                      </_DoneBtn>
                    )
                  }
                </_BottomRow>
              </_InputBox>
              <_SearchContainer
                onClick={e => e.stopPropagation()}
                isFilterSearchOpen={isFilterSearchOpen}
                extraPadding={inputValue}
                isMobileApp={isMobileApp}
                winHeigh={winHeight}
              >
                { showMobileVersion &&
                  <DiscoverAreaCta
                    areas={areas}
                    setDiscoveryAreaOpenState={setDiscoveryAreaOpenState}
                    onCloseSearch={onCloseSearch}
                    showDiscoverArea={!inputValue}
                  />
                }
                <PrevSearches
                  showPrevSearches={!inputValue}
                  onSuggestionSelect={onSuggestionSelect}
                  areaFilterTags={areaFilterTags}
                  stateUserPrevSearches={stateUserPrevSearches}
                />
                <PopularAreasSearchApiContainer
                  showPopSearches={!inputValue}
                  onSuggestionSelect={onSuggestionSelect}
                />
              </_SearchContainer>
            </>
            :
            <TagManager
              updateSelectedFiltersList={updateSelectedFiltersList}
              lastSearch={lastSearch}
            />
          }
        </div>
      </_FilterSearchInnerBox>
    </_FilterSearch>
  )
}

FilterSearch.propTypes = {
  result: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
  isFetching: PropTypes.bool.isRequired,
  loadAutoComplete: PropTypes.func.isRequired,
  typingStart: PropTypes.func.isRequired,

  addressIds: PropTypes.shape({}),

  areaFilterTags: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
  })),
  updateSelectedFiltersList: PropTypes.func,
  updateAlgoliaData: PropTypes.func,
  googleAnalyticsEvent: PropTypes.func,
  calculateLcaAndSelectedAreas: PropTypes.func,
  selectedItems: PropTypes.shape({}),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  lookups: PropTypes.shape({
    component: PropTypes.string,
    classType: PropTypes.string,
  }),
  userCurrentArea: PropTypes.shape({
    areaId: PropTypes.number,
    areaType: PropTypes.string,
  }),

  lookupFilterTag: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    area_id: PropTypes.number,
    type: PropTypes.string,
  })),
  areas: PropTypes.shape({
    url: PropTypes.string,
    label: PropTypes.string,
  }),
  lastSearch: PropTypes.string,
  isFilterSearchOpen: PropTypes.bool,
  setSelectedAreas: PropTypes.func,
  setFilterSearchOpenState: PropTypes.func,
  lookupFetching: PropTypes.bool,
  winWidth: PropTypes.number,
  winHeight: PropTypes.number,
  className: PropTypes.string,
  setDiscoveryAreaOpenState: PropTypes.func,
  isMobileApp: PropTypes.bool,
  fromNav: PropTypes.bool,
}

export default FilterSearch

